import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Check, BookOpen, Dumbbell, ChevronRight, Flame } from "lucide-react";
import { api } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { DimensionBadge, LevelBadge } from "@/components/Badges";
import { WeeklyGoal } from "@/components/WeeklyGoal";
import { goldBurst } from "@/lib/confetti";
import { Loader } from "@/components/Loader";
import { DAY_STATUS } from "@/components/c2g/labels";

export default function Percorso() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const celebrated = useRef(false);

  useEffect(() => {
    api.get("/path").then(({ data }) => {
      setData(data);
      if (!data?.engine && data?.weekly_goal?.achieved && !celebrated.current) {
        celebrated.current = true;
        setTimeout(goldBurst, 500);
      }
    }).catch(() => {});
  }, []);

  if (!data) return <Loader label="Apro il tuo percorso…" />;

  const { profile, progress, streak, available_day } = data;
  const completed = data.engine ? data.stats.days_lived : Object.values(progress).filter((d) => d.completed).length;
  return (
    <AppShell title="Il tuo percorso" greeting={`Settimana ${data.week || 1} · 7 giorni`}>
      <div className="px-5 pt-6">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl border border-[#D4AF37]/25 bg-gradient-to-br from-[#18181C] to-[#0D0D0F] p-5"
        >
          <div className="flex items-center gap-2">
            <DimensionBadge dimension={profile.dimension} title={profile.dimension_title} />
            <LevelBadge level={profile.level} title={profile.level_title} />
          </div>
          <h2 className="font-serif-el mt-3 text-2xl font-semibold text-[#FAF9F6]">{profile.archetype.title}</h2>
          {profile.week_title && <p className="mt-1 text-sm font-semibold text-[#E5C158]" data-testid="week-title">Settimana {profile.week}: {profile.week_title}</p>}
          <div className="mt-4 flex items-center gap-4">
            <div className="flex items-center gap-2 rounded-xl border border-[#27272A] bg-[#121215] px-3 py-2">
              <Flame size={18} className="text-[#D4AF37]" />
              <span className="tabular-nums font-semibold text-lg font-semibold text-[#FAF9F6]" title={data.engine ? "Giorni conclusi" : "Giorni vissuti"}>{streak}/7</span>
            </div>
            <div className="flex-1">
              <div className="mb-1 flex justify-between text-sm text-[#B4B4BC]">
                <span>Progresso</span>
                <span className="tabular-nums font-semibold">{completed}/7</span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-[#27272A]">
                <motion.div
                  className="h-full rounded-full bg-gradient-to-r from-[#E5C158] to-[#997A20]"
                  initial={{ width: 0 }}
                  animate={{ width: `${(completed / 7) * 100}%` }}
                />
              </div>
            </div>
          </div>
        </motion.div>

        {data.engine ? (
          <p className="mt-4 text-sm text-[#B4B4BC]" data-testid="c2g-percorso-profile"><span className="font-bold uppercase text-[#F4F4F5]">{profile.c2g.dimension}</span> <span className="rounded-md border border-[#D4AF37] px-1.5 py-0.5 text-[12px] font-bold uppercase text-[#E5C158]">{profile.c2g.level_label}</span> · <b>Obiettivo:</b> {profile.c2g.objective}</p>
        ) : (
          <div className="mt-4">
            <WeeklyGoal goal={data.weekly_goal} profile={profile} streak={streak} />
          </div>
        )}

        <div className="mt-6 space-y-3">
          {profile.days.map((d) => {
            const dp = progress[String(d.day)] || {};
            const cal = data.calendar?.days?.[d.day - 1] || {};
            const status = cal.status || (d.day > available_day ? "locked" : dp.completed ? "done" : "today");
            const locked = status === "locked";
            const done = status === "done";
            const current = status === "today";
            const STATUS = data.engine ? DAY_STATUS : { done: "Vissuto", today: "Oggi", missed: "Da recuperare", skipped: "Saltato", pending: "In attesa di conferma", locked: "Si apre quel giorno" };
            return (
              <button
                key={d.day}
                disabled={locked}
                onClick={() => navigate(`/percorso/giorno/${d.day}`)}
                data-testid={`day-card-${d.day}`}
                className={`flex w-full items-center gap-3 rounded-2xl border p-4 text-left transition ${ locked ?"border-[#27272A]/60 bg-[#0D0D0F] opacity-60" : "border-[#27272A] bg-[#121215] active:scale-[0.99]"
                } ${current ? "ring-1 ring-[#D4AF37]/50" : ""}`}
              >
                <span
                  className="tabular-nums font-semibold flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-base font-semibold"
                  style={
                    done
                      ? { backgroundColor: "rgba(16,185,129,0.15)", color: "#10B981" }
                      : current
                      ? { background: "linear-gradient(135deg,#E5C158,#997A20)", color: "#09090B" }
                      : { backgroundColor: "#18181C", color: "#8A8E96" }
                  }
                >
                  {done ? <Check size={18} /> : (cal.label || "").split(" ")[1] || d.day}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[12px] font-semibold uppercase tracking-wider text-[#8A8E96]" data-testid={`day-card-label-${d.day}`}>
                    {cal.label || `Giorno ${d.day}`}
                    <span className={`ml-2 normal-case tracking-normal ${current ? "text-[#E5C158]" : status === "missed" ? "text-[#F59E0B]" : "text-[#8A8E96]"}`}>· {STATUS[status]}</span>
                  </p>
                  <p className="truncate text-lg text-[#F4F4F5] font-semibold">{d.title}</p>
                  <div className="mt-0.5 flex gap-3 text-[12px] text-[#8A8E96]">
                    {d.has_lettura && <span className="inline-flex items-center gap-1"><BookOpen size={11} /> Lettura</span>}
                    {d.has_esercizio && <span className="inline-flex items-center gap-1"><Dumbbell size={11} /> Esercizio</span>}
                  </div>
                </div>
                {locked ? <Lock size={16} className="shrink-0 text-[#8A8E96]" /> : <ChevronRight size={18} className="shrink-0 text-[#8A8E96]" />}
              </button>
            );
          })}
        </div>
      </div>
    </AppShell>
  );
}
